import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/api';
import './TracksBrowse.css';

const computeTotalHours = (phases = []) => {
  const totalMins = phases.reduce((sum, phase) => {
    const phaseMins = (phase.resources || []).reduce(
      (s, r) => s + (r.estimatedMinutes || 0),
      0
    );
    return sum + phaseMins;
  }, 0);
  return Math.round(totalMins / 60) || null;
};

const CATEGORY_META = {
  frontend: { bg: "rgba(37,99,235,0.08)", color: "#2563eb", label: "Frontend", icon: "🎨" },
  backend: { bg: "rgba(5,150,105,0.08)", color: "#059669", label: "Backend", icon: "⚙️" },
  fullstack: { bg: "rgba(124,58,237,0.08)", color: "#7c3aed", label: "Full Stack", icon: "💻" },
  vlsi: { bg: "rgba(245,158,11,0.08)", color: "#d97706", label: "VLSI", icon: "🔬" },
  python: { bg: "rgba(234,88,12,0.08)", color: "#ea580c", label: "Python", icon: "🐍" },
  hardware: { bg: "rgba(100,116,139,0.08)", color: "#475569", label: "Hardware", icon: "🔧" },
  exam: { bg: "rgba(220,38,38,0.08)", color: "#dc2626", label: "Exam Prep", icon: "📝" },
  other: { bg: "rgba(100,116,139,0.08)", color: "#64748b", label: "General", icon: "💡" },
};

const CATEGORIES = ["all", "frontend", "backend", "fullstack", "vlsi", "python", "hardware", "exam"];

export default function TracksBrowse() {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const navigate = useNavigate();

  useEffect(() => { fetchTracks(); }, []);

  const fetchTracks = async () => {
    try {
      const { data } = await api.get('/tracks');
      setTracks(Array.isArray(data) ? data : (data.tracks || []));
    } catch (err) {
      console.error("Failed to load tracks:", err);
      setError("Failed to load tracks. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="tb-page">
        <div className="tb-loading">
          <div className="tb-spinner" />
          <p>Loading tracks…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tb-page">
        <div className="tb-error">
          <span>⚠️</span>
          <p>{error}</p>
          <button onClick={fetchTracks}>Retry</button>
        </div>
      </div>
    );
  }

  const enrolledTracks = tracks.filter((t) => t.userEnrollment);

  const filteredTracks = tracks.filter((t) => {
    const matchesSearch =
      t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.tags?.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCat = activeCategory === "all" || t.category === activeCategory;
    return matchesSearch && matchesCat;
  });

  return (
    <div className="tb-page">

      {/* ── Header ───────────────────────────────────────────── */}
      <div className="tb-header">
        <div className="tb-header-text">
          <div className="tb-eyebrow">🎯 Curated Paths</div>
          <h1>Career Tracks</h1>
          <p>
            Structured learning paths — phases, real projects, AI concept
            checks, and a shareable Proof Profile at the end.
          </p>
        </div>
        <div className="tb-header-meta">
          <div className="tb-stat">
            <span className="tb-stat-num">{tracks.length}</span>
            <span className="tb-stat-label">Tracks available</span>
          </div>
          <div className="tb-stat">
            <span className="tb-stat-num">Free</span>
            <span className="tb-stat-label">Phase 1 always free</span>
          </div>
        </div>
      </div>

      {/* ── My Tracks (enrolled progress) ────────────────────── */}
      {enrolledTracks.length > 0 && (
        <div className="tb-my-tracks">
          <h2 className="tb-my-tracks-title">My Tracks</h2>
          <div className="tb-my-tracks-list">
            {enrolledTracks.map((track) => {
              const cat = CATEGORY_META[track.category] || CATEGORY_META.other;
              const enr = track.userEnrollment;
              return (
                <div
                  key={track._id}
                  className="tb-my-track-row"
                  onClick={() => navigate(`/tracks/${track.slug}`)}
                >
                  <div className="tb-my-track-left">
                    <div
                      className="tb-my-track-icon"
                      style={{ background: cat.bg, color: cat.color }}
                    >
                      {cat.icon}
                    </div>
                    <div className="tb-my-track-info">
                      <div className="tb-my-track-name">{track.title}</div>
                      <div className="tb-my-track-sub">
                        {enr.completed ? "Completed 🏆" : `Phase ${Math.min(enr.currentPhase, track.phases?.length || 0)} of ${track.phases?.length || 0}`}
                      </div>
                    </div>
                  </div>
                  <div className="tb-my-track-right">
                    <div className="tb-my-track-bar-wrap">
                      <div className="tb-my-track-bar">
                        <div
                          className="tb-my-track-bar-fill"
                          style={{ width: `${enr.progressPercent}%` }}
                        />
                      </div>
                      <span className="tb-my-track-pct">
                        {enr.progressPercent}%
                      </span>
                    </div>
                    <div className="tb-my-track-readiness">
                      Readiness: <strong>{enr.readinessScore}%</strong>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Filters Bar ──────────────────────────────────────── */}
      <div className="tb-filters-bar">
        <div className="tb-search-wrap">
          <span className="tb-search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search tracks, tags…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="tb-search-input"
          />
          {searchTerm && (
            <button
              className="tb-search-clear"
              onClick={() => setSearchTerm("")}
            >
              ×
            </button>
          )}
        </div>
        <div className="tb-categories">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              className={`tb-cat-pill ${activeCategory === cat ? "active" : ""}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat === "all"
                ? "All"
                : (CATEGORY_META[cat]?.icon || "") +
                " " +
                (CATEGORY_META[cat]?.label || cat)}
            </button>
          ))}
        </div>
      </div>

      {/* ── Free callout ─────────────────────────────────────── */}
      <div className="tb-free-banner">
        <span>✅</span>
        <p>
          <strong>Phase 1 of every Track is completely free.</strong>
          &nbsp;Upgrade to Pro to unlock all phases, AI concept checks, and
          your public Proof Profile.
        </p>
      </div>

      {/* ── Grid ─────────────────────────────────────────────── */}
      {filteredTracks.length === 0 ? (
        <div className="tb-empty">
          <span>🗺️</span>
          <p>
            {searchTerm
              ? `No tracks match "${searchTerm}".`
              : "No tracks yet — check back soon."}
          </p>
          {searchTerm && (
            <button
              className="tb-empty-clear"
              onClick={() => setSearchTerm("")}
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <div className="tb-grid">
          {filteredTracks.map((track, i) => {
            const cat = CATEGORY_META[track.category] || CATEGORY_META.other;
            const hours = computeTotalHours(track.phases);
            const phases = track.phases?.length || 0;
            const enr = track.userEnrollment;

            return (
              <div
                key={track._id}
                className={`tb-card ${enr ? "tb-card--enrolled" : ""}`}
                style={{ animationDelay: `${i * 0.05}s` }}
                onClick={() => navigate(`/tracks/${track.slug}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" && navigate(`/tracks/${track.slug}`)
                }
              >
                {/* Thumbnail */}
                <div className="tb-card-hero">
                  {track.thumbnail ? (
                    <img
                      src={track.thumbnail}
                      alt={track.title}
                      className="tb-card-thumb"
                    />
                  ) : (
                    <div
                      className="tb-card-thumb-placeholder"
                      style={{ background: cat.bg }}
                    >
                      <span style={{ color: cat.color, fontSize: "34px" }}>
                        {cat.icon}
                      </span>
                    </div>
                  )}
                  <div className="tb-card-badges">
                    <span
                      className="tb-cat-badge"
                      style={{ background: cat.bg, color: cat.color }}
                    >
                      {cat.label}
                    </span>
                    {track.isPro && (
                      <span className="tb-pro-badge">PRO</span>
                    )}
                    {enr?.completed && (
                      <span className="tb-done-badge">🏆 Done</span>
                    )}
                  </div>

                  {/* Enrolled progress overlay on thumbnail */}
                  {enr && !enr.completed && (
                    <div className="tb-card-progress-overlay">
                      <div className="tb-card-progress-bar">
                        <div
                          className="tb-card-progress-fill"
                          style={{ width: `${enr.progressPercent}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Body */}
                <div className="tb-card-body">
                  <h3 className="tb-card-title">{track.title}</h3>
                  <p className="tb-card-desc">{track.description}</p>

                  {/* Stats */}
                  <div className="tb-card-stats">
                    <span className="tb-card-stat">
                      <span className="tb-stat-icon">📚</span>
                      {phases} phase{phases !== 1 ? "s" : ""}
                    </span>
                    {hours && (
                      <span className="tb-card-stat">
                        <span className="tb-stat-icon">⏱</span>
                        ~{hours}h content
                      </span>
                    )}
                    {track.estimatedMonths && (
                      <span className="tb-card-stat">
                        <span className="tb-stat-icon">📅</span>
                        {track.estimatedMonths}mo est.
                      </span>
                    )}
                    {track.enrollmentCount > 0 && (
                      <span className="tb-card-stat">
                        <span className="tb-stat-icon">👥</span>
                        {track.enrollmentCount}
                      </span>
                    )}
                  </div>

                  {/* Tags */}
                  {track.tags?.length > 0 && (
                    <div className="tb-card-tags">
                      {track.tags.slice(0, 4).map((tag) => (
                        <span key={tag} className="tb-tag">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Footer: enrolled vs not */}
                  <div className="tb-card-footer">
                    {enr ? (
                      <div className="tb-enrolled-footer">
                        <span className="tb-enrolled-label">
                          {enr.completed ? "Completed 🎉" : `Phase ${Math.min(enr.currentPhase, phases)}/${phases}`}
                        </span>
                        <span className="tb-readiness-label">
                          Readiness {enr.readinessScore}%
                        </span>
                        <button className="tb-card-btn">
                           {enr.completed ? "Review Profile →" : "Continue →"}
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="tb-free-label">Phase 1 free</span>
                        <button className="tb-card-btn">View Track →</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}